import { useEffect, useMemo, useState } from 'react';
import type { AircraftIconKind, FlightClass } from '@flightscanner/shared';
import { useStore } from '../store';
import { getAllAirlines, getAllCountries, getAllTypes, loadReference } from '../data/reference';
import { MultiCombobox } from '../ui/MultiCombobox';
import { cls } from '../lib/format';
import type { RichAircraft } from '../data/enrich';
import { enrichAll } from '../data/enrich';
import { fetchStates } from '../data/apiClient';
import type { RichSatellite } from '../data/types';
import type { RichVessel, VesselIconKind } from '../data/vesselEnrich';
import { mapBridge } from '../map/mapBridge';
import { ISS_NORAD_ID } from '../satellite/satelliteGlobeConstants';
import {
  AIRCRAFT_CLASSES as CLASSES,
  AIRCRAFT_ICON_KINDS as ICON_KINDS,
  SATELLITE_CLASSES,
  SATELLITE_KINDS,
  SATELLITE_REGIMES,
  VESSEL_CLASSES,
  VESSEL_ICON_KINDS,
} from './filterConstants';

interface Props {
  aircraft: RichAircraft[];
  vessels: RichVessel[];
  satellites: RichSatellite[];
  allSatellites: RichSatellite[];
}

const FILTER_TABS = [
  { id: 'aircraft' as const, label: 'Aircraft' },
  { id: 'vessels' as const, label: 'Vessels' },
  { id: 'satellites' as const, label: 'Satellites' },
];

export function FilterPanel({ aircraft, vessels, satellites, allSatellites }: Props) {
  const filter = useStore((s) => s.filter);
  const setFilter = useStore((s) => s.setFilter);
  const resetFilter = useStore((s) => s.resetFilter);
  const vesselFilter = useStore((s) => s.vesselFilter);
  const setVesselFilter = useStore((s) => s.setVesselFilter);
  const resetVesselFilter = useStore((s) => s.resetVesselFilter);
  const satelliteFilter = useStore((s) => s.satelliteFilter);
  const setSatelliteFilter = useStore((s) => s.setSatelliteFilter);
  const resetSatelliteFilter = useStore((s) => s.resetSatelliteFilter);
  const appMode = useStore((s) => s.appMode);
  const filterTab = useStore((s) => s.filterTab);
  const setFilterTab = useStore((s) => s.setFilterTab);
  const open = useStore((s) => s.filtersOpen);
  const setOpen = useStore((s) => s.setFiltersOpen);
  const focusAircraft = useStore((s) => s.focusAircraft);
  const focusSatellite = useStore((s) => s.focusSatellite);
  const setAppMode = useStore((s) => s.setAppMode);

  const [ready, setReady] = useState(false);
  const [presidentialGlobal, setPresidentialGlobal] = useState<RichAircraft[]>([]);
  const [presidentialLoading, setPresidentialLoading] = useState(false);
  const [presidentialCheckedAt, setPresidentialCheckedAt] = useState<number | null>(null);
  const [aircraftOnMap, setAircraftOnMap] = useState(() => mapBridge.getAircraftEnabled());
  const [vesselsOnMap, setVesselsOnMap] = useState(() => mapBridge.getVesselsEnabled());

  useEffect(() => {
    loadReference().then(() => setReady(true));
  }, []);

  useEffect(() => mapBridge.subscribeAircraft(setAircraftOnMap), []);
  useEffect(() => mapBridge.subscribeVessels(setVesselsOnMap), []);

  const airlineOptions = useMemo(
    () =>
      getAllAirlines()
        .map((a) => ({ value: a.icao, label: a.name, sub: a.iata ?? undefined }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [ready],
  );
  const typeOptions = useMemo(
    () => getAllTypes().map((t) => ({ value: t.icao, label: t.model || t.icao, sub: t.manufacturer || undefined })),
    [ready],
  );
  const countryOptions = useMemo(() => getAllCountries().map((c) => ({ value: c, label: c })), [ready]);
  const aircraftTypeOptions = useMemo(
    () =>
      [...typeOptions].sort((a, b) => {
        const aGroup = /airbus|boeing/i.test(a.label) ? 0 : 1;
        const bGroup = /airbus|boeing/i.test(b.label) ? 0 : 1;
        return aGroup - bGroup || a.label.localeCompare(b.label);
      }),
    [typeOptions],
  );

  const presidentialLocal = useMemo(
    () => aircraft.filter((a) => a.specialKind === 'presidential' && !a.onGround),
    [aircraft],
  );
  const presidentialFlights = useMemo(() => {
    const byIcao = new Map<string, RichAircraft>();
    for (const a of presidentialGlobal) byIcao.set(a.icao, a);
    for (const a of presidentialLocal) byIcao.set(a.icao, a);
    return [...byIcao.values()];
  }, [presidentialGlobal, presidentialLocal]);

  useEffect(() => {
    if (!open || !ready) return;
    const ctrl = new AbortController();
    let stopped = false;

    const load = async () => {
      setPresidentialLoading(true);
      try {
        const res = await fetchStates(
          { minLon: -180, minLat: -70, maxLon: 180, maxLat: 80 },
          2,
          { signal: ctrl.signal },
        );
        if (stopped) return;
        setPresidentialGlobal(enrichAll(res.aircraft).filter((a) => a.specialKind === 'presidential' && !a.onGround));
        setPresidentialCheckedAt(Date.now());
      } catch {
        if (!stopped) setPresidentialCheckedAt(Date.now());
      } finally {
        if (!stopped) setPresidentialLoading(false);
      }
    };

    void load();
    const id = setInterval(load, 60_000);
    return () => {
      stopped = true;
      ctrl.abort();
      clearInterval(id);
    };
  }, [open, ready]);

  const resetActiveTab = () => {
    if (filterTab === 'aircraft') resetFilter();
    else if (filterTab === 'vessels') resetVesselFilter();
    else resetSatelliteFilter();
  };

  const toggleClass = (c: FlightClass) => {
    const has = filter.classes.includes(c);
    setFilter({ classes: has ? filter.classes.filter((x) => x !== c) : [...filter.classes, c] });
  };

  const toggleIconKind = (kind: AircraftIconKind) => {
    const selected = filter.iconKinds ?? [];
    const has = selected.includes(kind);
    setFilter({ iconKinds: has ? selected.filter((x) => x !== kind) : [...selected, kind] });
  };

  const toggleVesselIconKind = (kind: VesselIconKind) => {
    const selected = vesselFilter.iconKinds;
    const has = selected.includes(kind);
    setVesselFilter({ iconKinds: has ? selected.filter((x) => x !== kind) : [...selected, kind] });
  };

  const toggleVesselClass = (id: string) => {
    const selected = vesselFilter.classes;
    const has = selected.includes(id);
    setVesselFilter({ classes: has ? selected.filter((x) => x !== id) : [...selected, id] });
  };

  const toggleSatelliteKind = (id: string) => {
    const selected = satelliteFilter.kinds;
    const has = selected.includes(id);
    setSatelliteFilter({ kinds: has ? selected.filter((x) => x !== id) : [...selected, id] });
  };

  const toggleSatelliteClass = (id: string) => {
    const selected = satelliteFilter.classes;
    const has = selected.includes(id);
    setSatelliteFilter({ classes: has ? selected.filter((x) => x !== id) : [...selected, id] });
  };

  const toggleSatelliteRegime = (id: string) => {
    const selected = satelliteFilter.regimes;
    const has = selected.includes(id);
    setSatelliteFilter({ regimes: has ? selected.filter((x) => x !== id) : [...selected, id] });
  };

  const focusPresidential = (icao: string) => {
    const ac = presidentialFlights.find((p) => p.icao === icao);
    focusAircraft(icao, ac?.lat != null && ac?.lon != null ? { lat: ac.lat, lon: ac.lon } : null);
    setOpen(false);
  };

  const iss = useMemo(
    () => allSatellites.find((s) => s.noradId === ISS_NORAD_ID) ?? null,
    [allSatellites],
  );

  const focusIss = () => {
    if (appMode !== 'satellite') setAppMode('satellite');
    focusSatellite(ISS_NORAD_ID);
    setOpen(false);
  };

  const formatIssLocation = (sat: RichSatellite) => {
    const lat = sat.position.lat.toFixed(1);
    const lon = sat.position.lon.toFixed(1);
    const latHem = sat.position.lat >= 0 ? 'N' : 'S';
    const lonHem = sat.position.lon >= 0 ? 'E' : 'W';
    return `${Math.abs(Number(lat)).toFixed(1)}°${latHem}, ${Math.abs(Number(lon)).toFixed(1)}°${lonHem}`;
  };

  const num = (v: string) => (v === '' ? null : Number(v));

  const tabDisabled = (tab: (typeof FILTER_TABS)[number]['id']) => {
    if (tab === 'satellites') return appMode === 'map';
    return appMode === 'satellite';
  };

  const tabTitle = (tab: (typeof FILTER_TABS)[number]['id']) => {
    if (tab === 'satellites' && appMode === 'map') return 'Open globe mode to filter satellites';
    return undefined;
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />}
      <aside
        className={cls(
          'scroll-thin fixed left-0 top-0 z-40 h-full w-[88vw] max-w-sm overflow-y-auto border-r border-bg-border bg-bg-panel/95 backdrop-blur transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-sm font-semibold tracking-wide text-slate-200">FILTERS</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={resetActiveTab}
              className="rounded-md px-2 py-1 text-xs text-slate-400 hover:text-slate-100"
            >
              Reset
            </button>
            <button onClick={() => setOpen(false)} className="rounded-md bg-bg-raised px-2 py-1 text-xs text-slate-200">
              Done
            </button>
          </div>
        </div>

        <div className="flex border-b border-bg-border px-4">
          {FILTER_TABS.map((tab) => {
            const disabled = tabDisabled(tab.id);
            const active = filterTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                disabled={disabled}
                title={tabTitle(tab.id)}
                onClick={() => !disabled && setFilterTab(tab.id)}
                className={cls(
                  'flex-1 border-b-2 px-2 py-2 text-xs font-medium transition-colors',
                  disabled && 'cursor-not-allowed opacity-40',
                  active ? 'border-accent text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-5 px-4 pb-8 pt-4">
          {appMode === 'map' && (
            <Section label="Show on map">
              <div className="space-y-2 rounded-md bg-bg-raised p-2.5 ring-1 ring-bg-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Aircraft</span>
                  <Toggle on={aircraftOnMap} onClick={() => mapBridge.setAircraftEnabled(!aircraftOnMap)} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Vessels</span>
                  <Toggle on={vesselsOnMap} onClick={() => mapBridge.setVesselsEnabled(!vesselsOnMap)} />
                </div>
              </div>
            </Section>
          )}

          {filterTab === 'aircraft' && appMode !== 'satellite' && (
            <>
              <Section label="Presidential watch">
                <div className="rounded-md bg-bg-raised p-2.5 ring-1 ring-bg-border">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-slate-100">
                        {presidentialFlights.length > 0
                          ? `${presidentialFlights.length} presidential aircraft airborne`
                          : presidentialLoading
                            ? 'Checking presidential aircraft...'
                            : 'No presidential aircraft airborne'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {presidentialCheckedAt
                          ? `Last checked ${new Date(presidentialCheckedAt).toLocaleTimeString()}`
                          : 'Checks the global live feed when filters open'}
                      </div>
                    </div>
                    <span
                      className={cls(
                        'h-2.5 w-2.5 shrink-0 rounded-full',
                        presidentialFlights.length
                          ? 'bg-yellow-300 shadow-[0_0_12px_rgba(253,224,71,0.9)]'
                          : presidentialLoading
                            ? 'bg-slate-400'
                            : 'bg-slate-700',
                      )}
                    />
                  </div>
                  {presidentialFlights.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {presidentialFlights.map((a) => (
                        <button
                          key={a.icao}
                          onClick={() => focusPresidential(a.icao)}
                          className="flex w-full items-center justify-between rounded-md bg-yellow-300/10 px-2 py-1.5 text-left text-xs text-yellow-100 ring-1 ring-yellow-300/30"
                        >
                          <span className="truncate">{a.callsign ?? a.registration ?? a.icao.toUpperCase()}</span>
                          <span className="ml-2 font-mono text-yellow-200">Focus</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Section>

              <Section label="Search">
                <input
                  value={filter.query}
                  onChange={(e) => setFilter({ query: e.target.value })}
                  placeholder="Callsign, reg, type, airline…"
                  className="w-full rounded-md border border-bg-border bg-bg-raised px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
                />
              </Section>

              <Section label="Color / aircraft kind">
                <div className="grid grid-cols-2 gap-1.5">
                  {ICON_KINDS.map((kind) => {
                    const on = (filter.iconKinds ?? []).includes(kind.id);
                    return (
                      <button
                        key={kind.id}
                        onClick={() => toggleIconKind(kind.id)}
                        className={cls(
                          'flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 shrink-0 rounded-full', kind.color)} />
                        <span className="truncate">{kind.label}</span>
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Category">
                <div className="flex flex-wrap gap-1.5">
                  {CLASSES.map((c) => {
                    const on = filter.classes.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleClass(c.id)}
                        className={cls(
                          'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 rounded-full', c.color)} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Airline">
                <MultiCombobox
                  placeholder="Add airline (ICAO or name)"
                  options={airlineOptions}
                  selected={filter.airlines}
                  onChange={(v) => setFilter({ airlines: v })}
                />
              </Section>

              <Section label="Aircraft type">
                <MultiCombobox
                  placeholder="Add type (e.g. A320, B738)"
                  options={aircraftTypeOptions}
                  selected={filter.types}
                  onChange={(v) => setFilter({ types: v })}
                  maxShown={12}
                />
              </Section>

              <Section label="Altitude (ft)">
                <RangeInputs
                  min={filter.altMin}
                  max={filter.altMax}
                  onMin={(v) => setFilter({ altMin: num(v) })}
                  onMax={(v) => setFilter({ altMax: num(v) })}
                />
              </Section>

              <Section label="Ground speed (kn)">
                <RangeInputs
                  min={filter.speedMin}
                  max={filter.speedMax}
                  onMin={(v) => setFilter({ speedMin: num(v) })}
                  onMax={(v) => setFilter({ speedMax: num(v) })}
                />
              </Section>

              <Section label="Origin country">
                <MultiCombobox
                  placeholder="Add country"
                  options={countryOptions}
                  selected={filter.countries}
                  onChange={(v) => setFilter({ countries: v })}
                />
              </Section>

              <Section label="Status">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Emergencies only</span>
                  <Toggle on={filter.emergencyOnly} onClick={() => setFilter({ emergencyOnly: !filter.emergencyOnly })} />
                </div>
                <div className="mt-2 flex gap-1.5">
                  {(['any', 'airborne', 'ground'] as const).map((g) => {
                    const val = g === 'any' ? null : g === 'ground';
                    const on = filter.onGround === val;
                    return (
                      <button
                        key={g}
                        onClick={() => setFilter({ onGround: val })}
                        className={cls(
                          'flex-1 rounded-md px-2 py-1 text-xs capitalize ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
              </Section>
            </>
          )}

          {filterTab === 'vessels' && appMode !== 'satellite' && (
            <>
              <Section label="Search">
                <input
                  value={vesselFilter.query}
                  onChange={(e) => setVesselFilter({ query: e.target.value })}
                  placeholder="Name, MMSI, IMO, destination…"
                  className="w-full rounded-md border border-bg-border bg-bg-raised px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
                />
              </Section>

              <Section label="Vessel kind">
                <div className="grid grid-cols-2 gap-1.5">
                  {VESSEL_ICON_KINDS.map((kind) => {
                    const on = vesselFilter.iconKinds.includes(kind.id);
                    return (
                      <button
                        key={kind.id}
                        onClick={() => toggleVesselIconKind(kind.id)}
                        className={cls(
                          'flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 shrink-0 rounded-full', kind.color)} />
                        <span className="truncate">{kind.label}</span>
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Category">
                <div className="flex flex-wrap gap-1.5">
                  {VESSEL_CLASSES.map((c) => {
                    const on = vesselFilter.classes.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleVesselClass(c.id)}
                        className={cls(
                          'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 rounded-full', c.color)} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Speed (kn)">
                <RangeInputs
                  min={vesselFilter.speedMin}
                  max={vesselFilter.speedMax}
                  onMin={(v) => setVesselFilter({ speedMin: num(v) })}
                  onMax={(v) => setVesselFilter({ speedMax: num(v) })}
                />
              </Section>
            </>
          )}

          {filterTab === 'satellites' && appMode === 'satellite' && (
            <>
              <Section label="ISS watch">
                <div className="rounded-md bg-bg-raised p-2.5 ring-1 ring-bg-border">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-100">
                        {iss ? iss.name : 'ISS not in catalog'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {iss
                          ? `${Math.round(iss.altitudeKm)} km · ${formatIssLocation(iss)} · NORAD ${ISS_NORAD_ID}`
                          : 'TLE missing from active catalog — trying stations group on refresh'}
                      </div>
                    </div>
                    <span
                      className={cls(
                        'h-2.5 w-2.5 shrink-0 rounded-full',
                        iss ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.9)]' : 'bg-slate-700',
                      )}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={focusIss}
                    disabled={!iss}
                    className={cls(
                      'mt-2 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs ring-1',
                      iss
                        ? 'bg-amber-400/10 text-amber-100 ring-amber-400/30 hover:bg-amber-400/15'
                        : 'cursor-not-allowed text-slate-500 ring-bg-border',
                    )}
                  >
                    <span className="truncate">International Space Station</span>
                    <span className="ml-2 font-mono text-amber-200">Focus</span>
                  </button>
                </div>
              </Section>

              <Section label="Search">
                <input
                  value={satelliteFilter.query}
                  onChange={(e) => setSatelliteFilter({ query: e.target.value })}
                  placeholder="Name, NORAD ID, owner…"
                  className="w-full rounded-md border border-bg-border bg-bg-raised px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
                />
              </Section>

              <Section label="Satellite kind">
                <div className="grid grid-cols-2 gap-1.5">
                  {SATELLITE_KINDS.map((kind) => {
                    const on = satelliteFilter.kinds.includes(kind.id);
                    return (
                      <button
                        key={kind.id}
                        onClick={() => toggleSatelliteKind(kind.id)}
                        className={cls(
                          'flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 shrink-0 rounded-full', kind.color)} />
                        <span className="truncate">{kind.label}</span>
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Class">
                <div className="flex flex-wrap gap-1.5">
                  {SATELLITE_CLASSES.map((c) => {
                    const on = satelliteFilter.classes.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleSatelliteClass(c.id)}
                        className={cls(
                          'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        <span className={cls('h-2 w-2 rounded-full', c.color)} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Orbit regime">
                <div className="flex flex-wrap gap-1.5">
                  {SATELLITE_REGIMES.map((r) => {
                    const on = satelliteFilter.regimes.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        onClick={() => toggleSatelliteRegime(r.id)}
                        className={cls(
                          'rounded-full px-2.5 py-1 text-xs ring-1',
                          on ? 'bg-bg-raised text-slate-100 ring-accent' : 'text-slate-400 ring-bg-border',
                        )}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section label="Altitude (km)">
                <RangeInputs
                  min={satelliteFilter.altMin}
                  max={satelliteFilter.altMax}
                  onMin={(v) => setSatelliteFilter({ altMin: num(v) })}
                  onMax={(v) => setSatelliteFilter({ altMax: num(v) })}
                />
              </Section>

              <Section label="Speed (km/s)">
                <RangeInputs
                  min={satelliteFilter.speedMin}
                  max={satelliteFilter.speedMax}
                  onMin={(v) => setSatelliteFilter({ speedMin: num(v) })}
                  onMax={(v) => setSatelliteFilter({ speedMax: num(v) })}
                />
              </Section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      {children}
    </div>
  );
}

function RangeInputs({
  min,
  max,
  onMin,
  onMax,
}: {
  min: number | null;
  max: number | null;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode="numeric"
        value={min ?? ''}
        onChange={(e) => onMin(e.target.value)}
        placeholder="min"
        className="w-full rounded-md border border-bg-border bg-bg-raised px-2 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
      />
      <span className="text-slate-500">–</span>
      <input
        type="number"
        inputMode="numeric"
        value={max ?? ''}
        onChange={(e) => onMax(e.target.value)}
        placeholder="max"
        className="w-full rounded-md border border-bg-border bg-bg-raised px-2 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-accent"
      />
    </div>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cls('relative h-5 w-9 rounded-full transition-colors', on ? 'bg-accent' : 'bg-bg-border')}
    >
      <span className={cls('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', on ? 'left-4' : 'left-0.5')} />
    </button>
  );
}
